import { Routes, Route, Link, NavLink, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useFoyrBundleAutoIngest } from "./lib/useFoyrBundleAutoIngest";
import Landing from "./routes/Landing";
import Rules from "./routes/Rules";
import Library from "./routes/Library";
import RuleBuilderPage from "./routes/RuleBuilder";
import Graph from "./routes/Graph";
import Login from "./routes/Login";
import Admin from "./routes/Admin";
import AdminPrompts from "./routes/AdminPrompts";
import AdminUsers from "./routes/AdminUsers";
import AdminMemory from "./routes/AdminMemory";
import AdminDatasets from "./routes/AdminDatasets";
import AdminOptimizer from "./routes/AdminOptimizer";
import AdminSystem from "./routes/AdminSystem";
import AdminStudio from "./routes/AdminStudio";
import IntegrationsPage from "./routes/IntegrationsPage";
import Connections from "./routes/Connections";
import Design from "./routes/Design";
import Catalog from "./routes/Catalog";
import CatalogImport from "./routes/CatalogImport";
import Moodboard from "./routes/Moodboard";
import { Logo } from "./components/Logo";
import { ProjectTracker } from "./components/header/ProjectTracker";
import { env } from "./lib/env";
import { cls } from "./lib/cls";
import { ArrowUpRight, LogOut, ShieldCheck } from "lucide-react";
import { getSession, clearSession, type Session } from "./lib/session";

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const loc = useLocation();
  const s = getSession();
  if (!s) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function RequireAdmin({ children }: { children: JSX.Element }): JSX.Element {
  const loc = useLocation();
  const s = getSession();
  if (!s) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (!s.isAdmin) return <Navigate to="/" replace />;
  return children;
}

function Header({ session, onSignOut }: { session: Session | null; onSignOut: () => void }): JSX.Element {
  return (
    <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-border/80">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-[64px] flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Logo />
        </Link>
        <nav className="flex items-center gap-0.5 sm:gap-1">
          {session ? (
            <>
              <NavLink to="/" end className={({ isActive }) => cls("nav-link", isActive && "active")}>
                Cockpit
              </NavLink>
              <NavLink to="/tracker" className={({ isActive }) => cls("nav-link", isActive && "active")}>
                Tracker
              </NavLink>
              <NavLink to="/design" className={({ isActive }) => cls("nav-link", isActive && "active")} title="2D floor-plan canvas">
                Design
              </NavLink>
              <NavLink to="/catalog" className={({ isActive }) => cls("nav-link", isActive && "active")} title="Browse the typed product catalog">
                Product&nbsp;catalog
              </NavLink>
              <NavLink to="/integrations" className={({ isActive }) => cls("nav-link", isActive && "active")}>
                Integrations
              </NavLink>
              <NavLink
                to="/rules"
                className={({ isActive }) => cls("nav-link", isActive && "active")}
                title="Active rules · Library · Builder · Drawings"
              >
                Rules
              </NavLink>
              <NavLink to="/graph" className={({ isActive }) => cls("nav-link", isActive && "active")}>
                Project&nbsp;map
              </NavLink>
              {session.isAdmin ? (
                <NavLink to="/admin" className={({ isActive }) => cls("nav-link inline-flex items-center gap-1", isActive && "active")}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Admin
                </NavLink>
              ) : null}
              <span
                className={cls(
                  "ml-2 hidden md:inline-flex items-center gap-1 pill border tabular",
                  session.isAdmin
                    ? "bg-foyr/10 border-foyr/40 text-foyr-deep font-semibold"
                    : "bg-cream-dark border-border text-ink-700",
                )}
                title={session.isAdmin ? "Admin · full configuration rights" : "Studio user"}
              >
                {session.isAdmin ? <ShieldCheck className="w-3 h-3" /> : null}
                {session.isAdmin ? <span className="text-[10px] uppercase tracking-wider mr-1">Admin</span> : null}
                <span className="text-[11.5px]">{session.email}</span>
              </span>
              <ProjectTracker />
              <button onClick={onSignOut} className="btn btn-ghost" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <a
              href={`mailto:${env.VITE_CONTACT_EMAIL}?subject=FoyrMind%20demo`}
              className="btn btn-primary ml-2"
            >
              Talk to us <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </nav>
      </div>
    </header>
  );
}

function Footer(): JSX.Element {
  return (
    <footer className="border-t border-border bg-cream-dark mt-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 flex items-center justify-between text-xs text-ink-500 gap-2">
        <a
          href={env.VITE_BRAND_SITE_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center hover:opacity-80 transition-opacity"
          title={env.VITE_BRAND_NAME}
        >
          <img src="/logo-lockup.svg" alt="HyperGraphMind" className="block h-6 w-auto" />
        </a>
        <div className="flex items-center gap-3">
          <a href={`mailto:${env.VITE_CONTACT_EMAIL}`} className="hover:text-ink-950">
            {env.VITE_CONTACT_EMAIL}
          </a>
          <span className="hidden sm:inline">Prove your reasoning.</span>
        </div>
      </div>
    </footer>
  );
}

export default function App(): JSX.Element {
  const [sess, setSess] = useState<Session | null>(getSession());
  useFoyrBundleAutoIngest();
  useEffect(() => {
    const i = setInterval(() => {
      const s = getSession();
      if ((s?.email ?? "") !== (sess?.email ?? "")) setSess(s);
    }, 1000);
    return () => clearInterval(i);
  }, [sess]);

  return (
    <div className="min-h-full flex flex-col bg-cream text-ink-950">
      <Header session={sess} onSignOut={() => { clearSession(); setSess(null); window.location.href = "/login"; }} />
      <main className="flex-1">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>}>
            {/* Onboard + Integrations moved to /integrations (with the
                Onboard sub-tab). Legacy URLs redirect there so existing
                bookmarks land on the new location. */}
            <Route index element={<Navigate to="/admin/prompts" replace />} />
            <Route path="onboard" element={<Navigate to="/integrations#onboard" replace />} />
            <Route path="integrations" element={<Navigate to="/integrations" replace />} />
            <Route path="prompts" element={<AdminPrompts />} />
            <Route path="optimizer" element={<AdminOptimizer />} />
            <Route path="memory" element={<AdminMemory />} />
            <Route path="datasets" element={<AdminDatasets />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="system" element={<AdminSystem />} />
          </Route>
          <Route path="/" element={<RequireAuth><Landing /></RequireAuth>} />
          <Route path="/tracker"  element={<RequireAuth><AdminStudio /></RequireAuth>} />
          <Route path="/practice" element={<Navigate to="/tracker" replace />} />
          <Route path="/studio"   element={<Navigate to="/tracker" replace />} />
          <Route path="/integrations" element={<RequireAuth><IntegrationsPage /></RequireAuth>} />
          <Route path="/connections" element={<RequireAuth><Connections /></RequireAuth>} />
          <Route path="/design" element={<RequireAuth><Design /></RequireAuth>} />
          <Route path="/catalog" element={<RequireAuth><Catalog /></RequireAuth>} />
          <Route path="/catalog-import" element={<RequireAuth><CatalogImport /></RequireAuth>} />
          <Route path="/moodboard" element={<RequireAuth><Moodboard /></RequireAuth>} />
          <Route path="/rules" element={<RequireAuth><Rules /></RequireAuth>} />
          <Route path="/rules/builder" element={<RequireAuth><RuleBuilderPage /></RequireAuth>} />
          <Route path="/library" element={<RequireAuth><Library /></RequireAuth>} />
          <Route path="/graph" element={<RequireAuth><Graph /></RequireAuth>} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}
